import React, { useEffect, useMemo, useState } from 'react';
import * as THREE from 'three';
import { Text } from '@react-three/drei';
import { useWorldStore, ZOOM } from '../state/worldStore';
import { ROOMS } from '../rooms';
import { doorPose } from '../city/venueRegistry';
import { baseGroundY, onGround } from '../city/cityGround';

// VenueFacades — the street face of each venue the town voted for: its door, the
// light over it, and its NAME above it (plan step 5: "the World adds the
// building, its door, its sign and its map label without a deploy").
//
// A registry venue stands on an existing city building (⚙ → Sites, phase 1), so
// the building is already there, solid and lit; what it lacks is anything that
// says this one is a club now. That is all this draws, at the door the site
// names, turned to the street (local +z = out, like every other site).
//
// HOURS (Khari's decision 4: "outside them it is closed and dark"): open, the
// lamp is warm, a line of light leaks under the door, a soft pool lies on the
// pavement and the name is lit in the venue's colour. Shut, all of it goes dark
// and the name is a shadow on the board — still readable up close, so you know
// what it is and the door tells you when it opens.
//
// ⚠ THIS WORLD IS NOT IN METRES: a person is ~0.75 units, so a door is ~0.95
// tall. No real lights (every point light costs every lit pixel, and the plaza
// already stuttered on a phone): everything here is unlit colour and one shared
// additive texture, and the bloom pass does the glow.

const DOOR = { w: 0.62, h: 0.95 };
const LAMP_Y = 1.06;
const SIGN_Y = 1.42;
const SIGN_H = 0.44;
const WARM = '#ffcf8a';
const DARK = '#15110e';

// One soft round pool for every door in the city, made on first use.
let poolTex = null;
function pool() {
  if (poolTex) return poolTex;
  const c = document.createElement('canvas'); c.width = 64; c.height = 64;
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(32, 32, 2, 32, 32, 32);
  grd.addColorStop(0, 'rgba(255,200,140,0.9)'); grd.addColorStop(0.5, 'rgba(255,170,100,0.3)'); grd.addColorStop(1, 'rgba(255,150,80,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 64, 64);
  poolTex = new THREE.CanvasTexture(c);
  poolTex.colorSpace = THREE.SRGBColorSpace;
  return poolTex;
}

function Facade({ slug, venue, ov, open, ground }) {
  const room = ROOMS[slug];
  const accent = room?.accent || '#ffb25e';
  const p = doorPose(venue.site, ov);
  const y = useMemo(() => baseGroundY(p.x, p.z), [p.x, p.z, ground]); // eslint-disable-line react-hooks/exhaustive-deps
  // The board fits the name, and never runs wider than the building's front.
  const signW = Math.min(Math.max(1.3, venue.name.length * 0.15 + 0.5), Math.max(1.3, Math.min(4.2, venue.site.w * 0.9)));
  return (
    <group position={[p.x, y, p.z]} rotation={[0, p.rotY, 0]}>
      {/* The door: dark, with a frame in the venue's colour so it reads as a
          door on a facade that was not drawn with one. */}
      <mesh position={[0, DOOR.h / 2, 0.03]} raycast={() => null}>
        <planeGeometry args={[DOOR.w, DOOR.h]} />
        <meshBasicMaterial color="#0a0809" />
      </mesh>
      {[[-1, 0], [1, 0]].map(([sx]) => (
        <mesh key={sx} position={[sx * (DOOR.w / 2 + 0.02), DOOR.h / 2, 0.034]} raycast={() => null}>
          <planeGeometry args={[0.035, DOOR.h + 0.04]} />
          <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
        </mesh>
      ))}
      <mesh position={[0, DOOR.h + 0.02, 0.034]} raycast={() => null}>
        <planeGeometry args={[DOOR.w + 0.075, 0.035]} />
        <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
      </mesh>
      {/* Light leaking out under it — the API's own words for a new venue. */}
      {open && (
        <mesh position={[0, 0.025, 0.036]} raycast={() => null}>
          <planeGeometry args={[DOOR.w - 0.04, 0.03]} />
          <meshBasicMaterial color={WARM} toneMapped={false} />
        </mesh>
      )}
      {/* The lamp over the door, and the pool it would throw. */}
      <mesh position={[0, LAMP_Y, 0.07]} raycast={() => null}>
        <boxGeometry args={[0.22, 0.06, 0.1]} />
        <meshBasicMaterial color={open ? WARM : DARK} toneMapped={false} />
      </mesh>
      {open && (
        <mesh position={[0, 0.015, 0.75]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null} renderOrder={2}>
          <planeGeometry args={[2.0, 1.6]} />
          <meshBasicMaterial map={pool()} transparent opacity={0.55} depthWrite={false} blending={THREE.AdditiveBlending} toneMapped={false} />
        </mesh>
      )}
      {/* THE NAME the town voted for, over the door, facing the street. Built
          like the stage's board (StageSign): a dark plate and drei Text. */}
      <group position={[0, SIGN_Y, 0.06]}>
        <mesh raycast={() => null}>
          <planeGeometry args={[signW, SIGN_H]} />
          <meshBasicMaterial color="#0c0a09" />
        </mesh>
        <mesh position={[0, -SIGN_H / 2 + 0.05, 0.004]} raycast={() => null}>
          <planeGeometry args={[signW * 0.78, 0.016]} />
          <meshBasicMaterial color={open ? accent : DARK} toneMapped={false} />
        </mesh>
        <Text
          position={[0, 0.03, 0.01]}
          fontSize={0.22}
          maxWidth={signW - 0.16}
          letterSpacing={0.06}
          color={open ? accent : '#3a322c'}
          anchorX="center"
          anchorY="middle"
          textAlign="center"
          outlineWidth={open ? 0.006 : 0}
          outlineColor="#000000"
        >
          {venue.name}
        </Text>
      </group>
    </group>
  );
}

export default function VenueFacades() {
  const registry = useWorldStore((s) => s.venueRegistry);
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const hours = useWorldStore((s) => s.venueOpen);
  // The ground's real heights land a few seconds after the city does.
  const [ground, setGround] = useState(0);
  useEffect(() => onGround(() => setGround((n) => n + 1)), []);
  // Not the one you are inside (2026-10-31): a sited room's windows would show
  // the back of its own door and sign standing in the doorway.
  const inside = useWorldStore((s) => (s.zoom === ZOOM.INTERIOR ? s.interior : null));
  const slugs = Object.keys(registry || {}).filter((k) => ROOMS[k]?.registry && !ROOMS[k].retired && k !== inside);
  if (!slugs.length) return null;
  return (
    <group>
      {slugs.map((k) => (
        <Facade key={k} slug={k} venue={registry[k]} ov={overrides?.[k] || null} open={hours?.[k]?.open !== false} ground={ground} />
      ))}
    </group>
  );
}
